import React, { useState } from 'react';
import { toast } from "sonner";
import { emailService } from '../Api/EmailService';
import { Button } from '../components/ui/button';
import { hasText, stripLeadingSpaces, trimFormData, emailError, errorClass, fieldA11y, escapeFormData } from '../lib/formValidation';
import BotTrap, { useBotTrap } from '../components/BotTrap';
import FieldError from '../components/FieldError';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ArrowRight, Clock, User, Calendar, BookOpen } from 'lucide-react';
import { blogPosts } from '../data/mockData';

// Shared field styling: gray field background, same blue as the card border on focus
const FIELD_CLASS =
  "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 " +
  "data-[placeholder]:text-slate-400 " +
  "focus:border-blue-500 focus:ring-1 focus:ring-blue-500 " +
  "focus-visible:border-blue-500 focus-visible:ring-1 focus-visible:ring-blue-500 " +
  // stop Chrome autofill from painting the field light blue
  "[&:-webkit-autofill]:[-webkit-text-fill-color:#fff] " +
  "[&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_#1e293b] " +
  "[&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_#1e293b]";

// Message for each required field (null = valid)
const validators = {
  email: (v) => emailError(v),
};

const BlogPage = () => {
  const featuredPost = blogPosts[0];

  const categories = [
    "AI & Automation",
    "Sales Strategy",
    "Customer Experience",
    "Implementation",
    "ROI & Analytics",
    "Training",
    "Mobile Technology",
    "Security"
  ];
  const [formData, setFormData] = useState({
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trap = useBotTrap();
  // Show a field's message only after the user has visited it
  const [fieldTouched, setFieldTouched] = useState({});
  // A field shows its message once the user has visited it or typed something,
  // so they always know why the button is disabled
  const getError = (field) => {
    const show = fieldTouched[field] || hasText(formData[field]);
    return show ? validators[field](formData[field]) : null;
  };
  // Subscribe is enabled only when the email field holds a valid email
  const canSubmit = !validators.email(formData.email);

  const createEmailPayload = (rawData) => {
    // everything typed by the visitor is escaped before it goes into the HTML email
    const { email } = escapeFormData(rawData);

    const subject = `New Newsletter Subscription from ${rawData.email}`;

    const template = `<!DOCTYPE html PUBLIC "-//W3C//DTD HTML 4.01 Transitional//EN">
<html>
  <head>
    <meta content="text/html;charset=UTF-8" http-equiv="Content-Type">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:ital,wght@0,100..900;1,100..900&display=swap" rel="stylesheet">
    <style>
      body {
        font-family: 'Noto Sans', sans-serif;
        color: #000000;
      }
      h2 {
        font-family: 'Noto Sans', sans-serif;
      }
      p {
        font-family: 'Noto Sans', sans-serif;
        margin: 4px 0;
      }
    </style>
  </head>
  <body>
    <div>
      <h2>New Newsletter Subscription</h2>
          <p><strong>Email:</strong> ${email}</p>
    </div>
  </body>
</html>`;
    return { subject, template };
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    if (trap.looksLikeBot()) {
      // pretend it worked, send nothing
      setFormData({ email: '' });
      setFieldTouched({});
      return;
    }
    setIsSubmitting(true)
    const payload = createEmailPayload(trimFormData(formData));
    const promise = emailService.sendEmail(payload); // this returns a promise

    // Show toast for this promise
    toast.promise(promise, {
      loading: "Subscribing you to the newsletter...",
      success: "You are now subscribed!",
      error: "Subscription failed. Please try again.",
    });

    // Handle the result of the promise
    try {
      await promise;
      setFormData({
        email: '',
      });
      // the empty field must not show "Email is required" straight after a successful signup
      setFieldTouched({});
    }
    catch (error) {
      setIsSubmitting(false)
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      // no leading spaces, so a field can never be "only spaces"
      [field]: stripLeadingSpaces(value)
    }));
  };

  // Trim trailing spaces when the user leaves any text field of the form
  const handleFormBlur = (e) => {
    const { id, value, tagName } = e.target;
    if (id in validators) setFieldTouched(prev => ({ ...prev, [id]: true }));
    if ((tagName === 'INPUT' || tagName === 'TEXTAREA') && id in formData && typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed !== value) handleChange(id, trimmed);
    }
  };
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <div className="flex items-center justify-center space-x-2 mb-6">
              <BookOpen className="h-6 w-6 text-blue-400" />
              <span className="text-blue-400 font-medium">CXSquare Blog</span>
            </div>
            <h1 className="display-lg text-white mb-6">
              CRM Insights & Best Practices
            </h1>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Stay ahead with the latest trends, strategies, and insights in customer relationship management.
              Learn from experts and discover how to maximize your CRM investment.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <div className="mb-12">
              <h2 className="h2 text-white mb-8">Featured Article</h2>
              <Card className="feature-card hover-card">
                <div className="grid lg:grid-cols-2 gap-8">
                  <div className="lg:order-2">
                    <div className="aspect-video bg-gradient-to-br from-blue-400/20 to-transparent rounded-xl flex items-center justify-center">
                      <div className="text-center">
                        <BookOpen className="h-16 w-16 text-blue-400 mx-auto mb-4" />
                        <p className="body-md text-slate-300">Featured Article</p>
                      </div>
                    </div>
                  </div>
                  <div className="lg:order-1 p-8">
                    <Badge className="bg-blue-400/10 text-blue-400 mb-4">
                      {featuredPost.category}
                    </Badge>
                    <h3 className="h1 text-white mb-4">{featuredPost.title}</h3>
                    <p className="body-lg text-slate-300 mb-6">{featuredPost.excerpt}</p>

                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center space-x-4 text-slate-400 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>{featuredPost.author}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4" />
                          <span>{new Date(featuredPost.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4" />
                          <span>{featuredPost.readTime}</span>
                        </div>
                      </div>
                    </div>
                    <a
                      href={featuredPost.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="btn-primary">
                        Read Full Article
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Categories */}
      {/* <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="h2 text-white text-center mb-12">Browse by Category</h2>
            <div className="grid md:grid-cols-4 gap-4">
              {categories.map((category, index) => (
                <Button
                  key={index}
                  variant="outline"
                  className="btn-secondary justify-start h-auto p-4"
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section> */}

      {/* Recent Posts */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="max-w-6xl mx-auto">
            <h2 className="h2 text-white mb-12">Latest Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, i) => (
                <Card key={i} className="feature-card hover-card">
                  <CardHeader>
                    <div className="aspect-video bg-gradient-to-br from-blue-400/10 to-transparent rounded-lg mb-4 flex items-center justify-center">
                      <BookOpen className="h-8 w-8 text-blue-400" />
                    </div>
                    <Badge className="bg-blue-400/10 text-blue-400 w-fit mb-2">
                      {post.category}
                    </Badge>
                    <CardTitle className="h3 text-white line-clamp-2">{post.title}</CardTitle>
                    <CardDescription className="body-md line-clamp-3">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-slate-400 text-sm mb-4">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="body-sm text-slate-400">
                        {new Date(post.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                      <a
                        href={post.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button className="btn-secondary">
                          Read More
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </a>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="display-md text-white mb-6">
              Stay Updated with CRM Insights
            </h2>
            <p className="body-lg mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter and get the latest CRM tips, strategies, and industry insights
              delivered directly to your inbox.
            </p>
            <form onSubmit={handleSubmit} onBlur={handleFormBlur} className="space-y-6">
              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  aria-label="Email address"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  required
                  placeholder="Enter your email"
                  {...fieldA11y('email', getError('email'))}
                  className={`${FIELD_CLASS} ${errorClass(getError('email'))} flex-1 h-auto px-4 py-3 rounded-lg`}
                />
                <Button type="submit" className="btn-primary whitespace-nowrap" disabled={isSubmitting || !canSubmit}>
                  {isSubmitting ? "Sending..." : "Subscribe"}
                </Button>
                {/* <Button className="btn-primary whitespace-nowrap">
                Subscribe
              </Button> */}
              </div>
              <FieldError id="email-error" message={getError('email')} />
              <BotTrap trap={trap} />
            </form>
          </div>
        </div>
      </section>

      {/* Back Navigation */}
      {/* <section className="section-padding section-dark">
        <div className="container">
          <div className="text-center">
            <Link to="/resources" className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Resources
            </Link>
          </div>
        </div>
      </section> */}
    </div>
  );
};
export default BlogPage;